import { AxiosInstance } from "axios";
import {
  GetEmailInboxCountersResponseI,
  GetEmailInboxRequestI,
  GetEmailInboxResponseI,
  GetEmailThreadResponseI,
} from "shared/lib/interfaces/email/inbox";
import {
  GetCallContextResponseI,
  SaveCallContextRequestParamsI,
  SaveCallContextResponseI,
} from "shared/lib/interfaces/email/call-context";

import {
  EmailHistoryItemI,
  EmailSequenceI,
} from "@/modules/email-templates-sidebar/interfaces";

export interface EmailTemplateI {
  id?: string;
  name?: string;
  is_custom?: boolean;
  prompt?: string;
  attach_one_pager?: boolean;
  attachments?: AttachmentI[];
  updated_at?: string;
}

export interface AttachmentI {
  id: string;
  name: string;
}

export interface GetEmailTemplatesListResponseI {
  email_templates: EmailTemplateI[];
}

export interface CreateEmailTemplateRequestParamsI {
  name: string;
  prompt: string;
  attach_one_pager?: boolean;
}

export interface CreateEmailTemplateResponseI {
  email_template: EmailTemplateI;
}

export interface CheckCampaignEmailSetupResponseI {
  is_email_setup: boolean;
}

export type UpdateEmailTemplateRequestParamsI =
  Partial<CreateEmailTemplateRequestParamsI>;

export interface GetEmailGenerationInfoRequestParamsI {
  /**
   * template id to use for email generation
   * as for example of email generation, template id is not needed
   * but if you want to use a specific template for email generation, you can pass the template id
   */
  email_template_id?: string;
  campaign_id: string;
  contact_id: string;
  account_id: string;
}

export interface GetEmailGenerationStatusResponseI {
  is_complete?: boolean;
  created_at?: string;
  subject?: string;
  body?: string;
  attachments?: AttachmentI[];
}

export interface ValidateEmailsRequestParamsI {
  emails: string[];
}

export interface ValidateEmailsResponseI {
  valid_emails: string[];
  invalid_emails: string[];
}

export interface EmailParticipantsRequestParamsI {
  to: string[];
  cc?: string[];
  bcc?: string[];

  //@deprecated
  max_follow_ups?: number;
  follow_up_interval_days?: number;
}

export interface SendEmailRequestParamsI extends EmailParticipantsRequestParamsI {
  contact_id: string;
  account_id: string;
  campaign_id: string;

  subject: string;
  body?: string; //plain_text

  attachments: string[];

  plain_body?: string;
  html_body: string;

  //Note: You can get it from the disposition response
  email_id?: string;

  max_follow_ups?: number;
  follow_up_interval_days?: number;
}

/**
 * This interface is used to send a sequence of emails
 * and is based on the one from the request
 * EmailSequenceItemI
 * but without the order field
 */
export interface SendEmailSequenceRequestParamI {
  email_id: string;
  from: string;
  to: string[];
  cc?: string[];
  bcc?: string[];
  subject: string;
  html_body: string;
  plain_body?: string;
  attachments?: string[];
  scheduled_at?: string;
}

export interface SendEmailSequenceRequestParamsI {
  /**
   * if true, the email will be sent even if the email check fails
   * on the BE needs to be set to true to track whether the email was sent to unverified email addresses
   */
  override_email_check?: boolean;

  emails?: SendEmailSequenceRequestParamI[];
}

export interface GetEmailGenerationInfoResponseI {
  campaign_id: string;
  from_email: string;
  /**
   * The sender's name, as glen renders it for the signature.
   *
   * Returned by the endpoint all along and never declared here, so the compose
   * flow could not forward it and the prompt rendered "Salesperson Name: None".
   * The model then signed off "[Salesperson Name]".
   */
  caller_name: string;
  campaign_name: string;
  contact_email: string;
  account_id: string;
  contact_id: string;
  contact_name: string;
  contact_title: string;
  account_name: string;
  account_industry: string;
  account_city: string;
  account_state: string;
  account_headcount: string;
  account_website: string;
  email_signature: string;
  user_prompt: string;
}

export interface GetShouldSendEmailResponseI {
  account_executive_email: string;
  should_send_email: boolean;
  email_subject: string;
  email_body: string;
  email_id: string;
}

export interface GetShouldSendEmailRequestParamsI {
  has_meeting?: boolean;
  had_conversation_with_prospect?: boolean;
  disposition?: string;
}

export interface GetEmailHistoryParamsI {
  next_token?: string;
}

export interface GetEmailHistoryResponseI {
  emails: EmailHistoryItemI[];
  next_token?: string | null;
}

export interface CreateEmailSequenceRequestParamsI {
  campaign_id: string;
  account_id: string;
  contact_id: string;
}

export interface CreateEmailSequenceResponseI {
  sequence_id: string;
  email_id: string;
}

export interface GetEmailSequenceResponseI {
  sequence_id: string;
  emails: EmailSequenceI;
}

export interface AddEmailToSequenceResponse {
  email_id: string;
}

export const EmailAPIEndpoints = (client: AxiosInstance) => ({
  getEmailTemplatesList: () =>
    client.get<GetEmailTemplatesListResponseI>(`/v1/email_template/list`),

  checkCampaignHasEmailAutomation: (campaignId: string) =>
    client.get<CheckCampaignEmailSetupResponseI>(
      `/v2/campaign/${campaignId}/email_is_setup`
    ),

  createEmailTemplate: (params: CreateEmailTemplateRequestParamsI) =>
    client.post<CreateEmailTemplateResponseI>(`/v1/email_template`, params),

  updateEmailTemplate: (
    templateId: string,
    params: UpdateEmailTemplateRequestParamsI
  ) => client.patch(`/v1/email_template/${templateId}`, params),

  deleteEmailTemplate: (templateId: string) =>
    client.delete(`/v1/email_template/${templateId}`),

  getEmailGenerationInfo: (
    templateId?: string,
    params?: GetEmailGenerationInfoRequestParamsI
  ) => {
    const templateIdParam = templateId ? { email_template_id: templateId } : {};

    return client.post<GetEmailGenerationInfoResponseI>(
      `/v1/email/fetch_info_for_email_generation`,
      { ...params, ...templateIdParam }
    );
  },

  validateEmails: (params: ValidateEmailsRequestParamsI) =>
    client.post<ValidateEmailsResponseI>(
      `/v1/email/confirm_email_address`,
      params
    ),

  sendEmail: (params: SendEmailRequestParamsI) => {
    const campaignId = params?.campaign_id;

    return client.post(`/v2/campaign/${campaignId}/email/send`, params);
  },

  getInbox: ({
    inbox_type,
    campaign_ids,
    search_term,
    next_token,
  }: GetEmailInboxRequestI) =>
    client.post<GetEmailInboxResponseI>("/v1/inbox", {
      inbox_type,
      campaign_ids,
      next_token,
      search_term: search_term || undefined,
    }),

  getInboxThread: (threadId: string, nextToken?: string | null) =>
    client.post<GetEmailThreadResponseI>(`/v1/inbox/thread/${threadId}`, {
      next_token: nextToken,
    }),

  getInboxCounters: (campaignIds: string[]) =>
    client.post<GetEmailInboxCountersResponseI>("/v1/inbox_stats", {
      campaign_ids: campaignIds,
    }),

  getShouldSendFollowUpEmail: (
    campaignId: string,
    callId: string,
    params: GetShouldSendEmailRequestParamsI
  ) =>
    client.post<GetShouldSendEmailResponseI>(
      `/v1/campaign/${campaignId}/call/${callId}/should_send_email`,
      params
    ),

  getEmailHistory: (
    campaignId: string,
    contactId: string,
    params: GetEmailHistoryParamsI
  ) =>
    client.post<GetEmailHistoryResponseI>(
      `/v1/campaign/${campaignId}/contact/${contactId}/email_history`,
      params
    ),

  createEmailSequence: (params: CreateEmailSequenceRequestParamsI) =>
    client.post<CreateEmailSequenceResponseI>(
      `/v1/email/create_sequence`,
      params
    ),

  getEmailSequence: (sequenceId: string) =>
    client.get<GetEmailSequenceResponseI>(`/v1/sequence/${sequenceId}`),

  addEmailToSequence: (sequenceId: string) =>
    client.post<AddEmailToSequenceResponse>(
      `/v1/sequence/${sequenceId}/new_follow_up_email`
    ),

  removeEmailFromSequence: (emailId: string) =>
    client.delete(`/v1/email/${emailId}`),

  sendEmailSequence: (
    sequenceId: string,
    params: SendEmailSequenceRequestParamsI
  ) => client.post(`/v1/sequence/${sequenceId}/send`, params),

  getCallContext: (campaignId: string, accountId: string, contactId: string) =>
    client.get<GetCallContextResponseI>(
      `/v1/campaign/${campaignId}/account/${accountId}/call_context`,
      { params: { contact_id: contactId } }
    ),

  saveCallContext: (
    campaignId: string,
    accountId: string,
    params: SaveCallContextRequestParamsI
  ) =>
    client.post<SaveCallContextResponseI>(
      `/v1/campaign/${campaignId}/account/${accountId}/call_context`,
      params
    ),
});
